// Sign-in screen — the front door of the app (staging./partner. hosts open here
// directly; the marketing site lives at powerbess.ai). Talks to auth-service via
// the F1 AuthProvider. Three steps:
//   credentials → mfa     (user who turned MFA on: verify a TOTP code)
//   credentials → setup   (MFA required by policy but not enrolled yet)
// The backend never issues tokens until the code is verified.

import { useEffect, useRef, useState } from "react";
import { useSplashDismiss } from "@/app/console/splash";
import type { ClipboardEvent, FormEvent, KeyboardEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, Eye, EyeOff, KeyRound, Loader2, Lock, Mail } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { PowerBESSLogo } from "@/components/PowerBESSLogo";
import { CONSOLE_APPS } from "@/app/products";
import { ProductIcon } from "@/app/console/ui";
import { useAuth } from "@/lib/auth/AuthProvider";
import { authClient } from "@/lib/auth/authClient";
import { isApiError } from "@/lib/api/errors";
import { secretFromProvisioningUri } from "@/lib/auth/totp";

type Phase =
  | { kind: "credentials" }
  | { kind: "mfa"; challengeToken: string }
  | { kind: "setup"; challengeToken: string; provisioningUri: string; qrSvg: string | null };

const PRODUCTS = CONSOLE_APPS.filter((a) => a.kind === "product" && a.id !== "overview");

function errText(err: unknown, fallback: string): string {
  return isApiError(err) ? err.message : err instanceof Error ? err.message : fallback;
}

export default function Login() {
  useSplashDismiss();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname?: string } } };

  const [phase, setPhase] = useState<Phase>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const next = location.state?.from?.pathname ?? "/overview";

  useEffect(() => {
    document.title = "Sign in · PowerBESS";
  }, []);

  // Already signed in → straight into the app.
  if (!auth.loading && auth.user && phase.kind === "credentials") {
    return <Navigate to={next} replace />;
  }

  async function onCredentials(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const result = await auth.login(email.trim(), password);
      if (result.kind !== "mfa_required") {
        navigate(next, { replace: true });
        return;
      }
      setCode("");
      if (result.mfaSetupRequired) {
        const setup = await authClient.setupMfa(result.challengeToken);
        setPhase({
          kind: "setup",
          challengeToken: result.challengeToken,
          provisioningUri: setup.provisioning_uri,
          qrSvg: setup.qr_svg ?? null,
        });
      } else {
        setPhase({ kind: "mfa", challengeToken: result.challengeToken });
      }
    } catch (err) {
      setErrorMessage(errText(err, "Sign-in failed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(value: string) {
    if (phase.kind !== "mfa" && phase.kind !== "setup") return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await auth.verifyMfa(phase.challengeToken, value);
      navigate(next, { replace: true });
    } catch (err) {
      const msg = errText(err, "Code verification failed");
      // A burned challenge (too many tries) needs a fresh sign-in.
      if (/sign in again|already used|expired/i.test(msg)) {
        setPhase({ kind: "credentials" });
        setPassword("");
      }
      setErrorMessage(msg);
      setCode("");
    } finally {
      setSubmitting(false);
    }
  }

  function backToCredentials() {
    setPhase({ kind: "credentials" });
    setErrorMessage(null);
    setCode("");
  }

  return (
    <div className="flex min-h-screen bg-surface-0 text-text-1">
      {/* ---- brand panel (desktop) ------------------------------------------------ */}
      {/* Sizes are fluid: each clamp() scales with BOTH viewport width and height
          (min(vw, vh)) between a floor and a ceiling, so the panel fits a 13"
          laptop, a short window and a 27" monitor without overflow or tiny text. */}
      <aside className="relative hidden w-1/2 max-w-[860px] flex-col justify-between overflow-hidden border-r border-line bg-surface-1 lg:flex"
        style={{ padding: "clamp(28px, min(3.4vw, 5.5vh), 64px)", gap: "clamp(16px, 3vh, 40px)" }}>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "48px 48px", maskImage: "radial-gradient(ellipse at 30% 40%, #000 20%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse at 30% 40%, #000 20%, transparent 75%)" }} />
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-brand/[0.05] blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 right-0 h-[420px] w-[420px] rounded-full bg-product-asset/[0.05] blur-3xl" />

        <a href="https://powerbess.ai" className="relative flex w-fit items-center gap-3">
          <PowerBESSLogo size={34} />
          <span className="font-semibold tracking-tight" style={{ fontSize: "clamp(1.125rem, min(1.4vw, 2.4vh), 1.5rem)" }}>
            Power<span className="text-brand">BESS</span>.ai
          </span>
        </a>

        <div className="relative" style={{ maxWidth: "min(100%, 34rem)" }}>
          <h1 className="font-semibold tracking-tight" style={{ fontSize: "clamp(1.875rem, min(2.9vw, 4.6vh), 3.25rem)", lineHeight: 1.12 }}>
            The intelligence layer for <span className="text-brand">grid‑scale batteries</span>.
          </h1>
          <p className="leading-relaxed text-text-2"
            style={{ marginTop: "clamp(10px, 2vh, 22px)", fontSize: "clamp(0.95rem, min(1.05vw, 1.9vh), 1.15rem)" }}>
            Forecast Indian power markets, stay DSM-compliant and run every battery at its best — in one platform.
          </p>
          <ul className="grid" style={{ marginTop: "clamp(16px, 4vh, 44px)", gap: "clamp(6px, 1.1vh, 12px)" }}>
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              return (
                <li key={p.id}
                  className="relative flex items-center overflow-hidden rounded-card border border-line bg-surface-0/60 transition-colors duration-fast hover:border-line-strong hover:bg-surface-2"
                  style={{ gap: "clamp(10px, 1vw, 16px)", padding: "clamp(7px, 1.25vh, 14px) clamp(10px, 1vw, 16px)" }}>
                  <span className="absolute inset-y-0 left-0 w-0.5" style={{ backgroundColor: p.accent }} aria-hidden="true" />
                  <ProductIcon icon={Icon} accent={p.accent} size="md"
                    style={{ width: "clamp(34px, min(2.7vw, 4.6vh), 48px)", height: "clamp(34px, min(2.7vw, 4.6vh), 48px)" }} />
                  <span className="min-w-0">
                    <span className="block font-semibold text-text-1" style={{ fontSize: "clamp(0.95rem, min(1.1vw, 2vh), 1.125rem)" }}>{p.name}</span>
                    <span className="block truncate text-text-2" style={{ fontSize: "clamp(0.78rem, min(0.85vw, 1.55vh), 0.9rem)" }}>{p.tagline}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <p className="relative text-text-3" style={{ fontSize: "clamp(0.75rem, 0.8vw, 0.875rem)" }}>
          Advisory platform — prices, grades and recommends; never executes trades.
        </p>
      </aside>

      {/* ---- form ------------------------------------------------------------------- */}
      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="w-full max-w-[400px] 2xl:max-w-[440px]">
          <a href="https://powerbess.ai" className="mb-10 flex items-center gap-2.5 lg:hidden">
            <PowerBESSLogo size={28} />
            <span className="text-lg font-semibold">Power<span className="text-brand">BESS</span>.ai</span>
          </a>

          {phase.kind === "credentials" && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight 2xl:text-3xl">Sign in</h2>
              <p className="mt-1.5 text-sm text-text-2 2xl:text-base">Welcome back. Use your work email to continue.</p>

              <form onSubmit={onCredentials} className="mt-8 space-y-5" noValidate>
                <Field id="email" label="Work email" icon={Mail}>
                  <input id="email" type="email" autoComplete="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                    className={inputCls} />
                </Field>

                <Field id="password" label="Password" icon={Lock}
                  aside={<Link to="/forgot-password" className="-my-3 inline-flex h-11 items-center text-xs text-text-3 underline-offset-2 hover:text-text-1 hover:underline sm:-my-2 sm:h-8 2xl:text-sm">Forgot password?</Link>}>
                  <input id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className={`${inputCls} pr-11`} />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-[6px] text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 sm:right-2 sm:h-8 sm:w-8">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </Field>

                <ErrorNote message={errorMessage} />

                <PrimaryButton busy={submitting} disabled={!email || !password} busyLabel="Signing in…">
                  Sign in <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </PrimaryButton>
              </form>

              <p className="mt-8 text-center text-xs text-text-3 2xl:text-sm">
                No account yet? Ask your organisation admin for an invite.
              </p>
            </>
          )}

          {phase.kind === "mfa" && (
            <>
              <StepIcon />
              <h2 className="text-2xl font-semibold tracking-tight 2xl:text-3xl">Two-step verification</h2>
              <p className="mt-1.5 text-sm text-text-2 2xl:text-base">
                Enter the 6-digit code from your authenticator app for <span className="text-text-1">{email}</span>.
              </p>
              <div className="mt-8 space-y-5">
                <OtpInput value={code} onChange={setCode} onComplete={verify} disabled={submitting} />
                <ErrorNote message={errorMessage} />
                <PrimaryButton busy={submitting} disabled={code.length !== 6} busyLabel="Verifying…" onClick={() => verify(code)}>
                  Verify and continue
                </PrimaryButton>
                <BackLink onClick={backToCredentials} />
              </div>
            </>
          )}

          {phase.kind === "setup" && (
            <>
              <StepIcon />
              <h2 className="text-2xl font-semibold tracking-tight 2xl:text-3xl">Secure your account</h2>
              <p className="mt-1.5 text-sm text-text-2 2xl:text-base">Two-step verification is required for this account.</p>

              <ol className="mt-6 space-y-5">
                <li>
                  <StepLabel n={1}>Scan with Google Authenticator, Authy or 1Password</StepLabel>
                  <div className="mt-3 flex items-center gap-4 rounded-card border border-line bg-surface-1 p-4">
                    <div className="shrink-0 rounded-lg bg-white p-2.5" aria-label="QR code for your authenticator app">
                      {phase.qrSvg
                        ? <div className="h-[132px] w-[132px] [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: phase.qrSvg }} />
                        : <QRCodeSVG value={phase.provisioningUri} size={132} />}
                    </div>
                    {secretFromProvisioningUri(phase.provisioningUri) && (
                      <div className="min-w-0 text-xs text-text-3">
                        Can&apos;t scan? Enter this key:
                        <code className="mt-1.5 block select-all break-all rounded-[6px] bg-surface-2 px-2 py-1.5 font-code text-[11px] text-text-2">
                          {secretFromProvisioningUri(phase.provisioningUri)}
                        </code>
                      </div>
                    )}
                  </div>
                </li>
                <li>
                  <StepLabel n={2}>Enter the 6-digit code it shows</StepLabel>
                  <div className="mt-3">
                    <OtpInput value={code} onChange={setCode} onComplete={verify} disabled={submitting} />
                  </div>
                </li>
              </ol>

              <div className="mt-6 space-y-4">
                <ErrorNote message={errorMessage} />
                <PrimaryButton busy={submitting} disabled={code.length !== 6} busyLabel="Verifying…" onClick={() => verify(code)}>
                  Enable and sign in
                </PrimaryButton>
                <BackLink onClick={backToCredentials} />
              </div>
            </>
          )}

          <div className="mt-10 flex items-center justify-center gap-4 text-xs text-text-3 2xl:text-sm">
            <a href="https://powerbess.ai" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">powerbess.ai</a>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">Terms</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------

const inputCls =
  "h-11 w-full rounded-control border border-line-strong bg-surface-2 pl-11 pr-3 text-sm 2xl:h-12 2xl:text-base text-text-1 placeholder:text-text-3 transition-colors duration-fast hover:border-white/25 focus:border-brand/60 focus:outline-none focus:ring-2 focus:ring-brand/25";

function Field({ id, label, icon: Icon, aside, children }: {
  id: string; label: string; icon: typeof Mail; aside?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label htmlFor={id} className="text-xs font-medium text-text-2 2xl:text-sm">{label}</label>
        {aside}
      </div>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-text-3" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}

function PrimaryButton({ busy, disabled, busyLabel, onClick, children }: {
  busy: boolean; disabled?: boolean; busyLabel: string; onClick?: () => void; children: React.ReactNode;
}) {
  return (
    <button type={onClick ? "button" : "submit"} onClick={onClick} disabled={busy || disabled}
      className="flex h-11 w-full items-center justify-center gap-2 rounded-control bg-brand text-sm font-semibold 2xl:h-12 2xl:text-base text-black transition-colors duration-fast hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0 disabled:cursor-not-allowed disabled:opacity-40">
      {busy ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{busyLabel}</> : children}
    </button>
  );
}

function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="flex items-start gap-2 rounded-control border border-danger/30 bg-danger/[0.06] px-3 py-2.5 text-sm text-danger">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function StepIcon() {
  return (
    <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-card border border-line bg-surface-2 text-text-1">
      <KeyRound className="h-5 w-5" aria-hidden="true" />
    </span>
  );
}

function StepLabel({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-text-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/[0.08] font-mono text-[11px] font-medium text-text-2">{n}</span>
      {children}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="mx-auto flex h-11 items-center gap-1.5 px-3 text-xs text-text-3 transition-colors duration-fast hover:text-text-1 sm:h-9">
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Use a different account
    </button>
  );
}

/** Six single-digit boxes; supports typing, backspace navigation and paste. */
function OtpInput({ value, onChange, onComplete, disabled }: {
  value: string; onChange: (v: string) => void; onComplete: (v: string) => void; disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    refs.current[Math.min(value.length, 5)]?.focus();
  }, [value.length]);

  function set(v: string) {
    const clean = v.replace(/\D/g, "").slice(0, 6);
    onChange(clean);
    if (clean.length === 6) onComplete(clean);
  }
  function onKey(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !value[i] && i > 0) {
      e.preventDefault();
      onChange(value.slice(0, i - 1));
    }
  }
  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    set(e.clipboardData.getData("text"));
  }
  return (
    <div className="flex justify-between gap-2" role="group" aria-label="Six digit verification code">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={6}
          disabled={disabled}
          value={value[i] ?? ""}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, "");
            if (d.length > 1) return set(value.slice(0, i) + d);
            set(value.slice(0, i) + d + value.slice(i + 1));
          }}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={onPaste}
          className="h-14 w-full min-w-0 rounded-control border border-line-strong bg-surface-2 text-center font-mono text-xl tabular-nums text-text-1 transition-colors duration-fast focus:border-brand/60 focus:outline-none focus:ring-2 focus:ring-brand/25 disabled:opacity-50"
        />
      ))}
    </div>
  );
}
